import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CallEnd as CallEndIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';
import { getApiUrl } from '../config/environment';

const API_BASE_URL = getApiUrl();

interface CallLog {
  id: string;
  assistantId: string;
  phoneNumberId: string;
  customerId: string;
  type: string;
  status: 'queued' | 'ringing' | 'in-progress' | 'forwarding' | 'ended';
  endedReason?: string;
  cost?: number;
  costBreakdown?: any;
  messages?: any[];
  phoneNumber?: {
    number: string;
    twilioPhoneNumber?: string;
  };
  customer?: {
    number: string;
  };
  assistant?: {
    name: string;
  };
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  endedAt?: string;
}

interface CallStats {
  total: number;
  successful: number;
  failed: number;
  transferred: number;
  endedReasons: Record<string, number>;
  totalCost: number;
  averageDuration: number;
}

const CallLogs: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [logs, setLogs] = useState<CallLog[]>([]);
  const [stats, setStats] = useState<CallStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    assistantId: '',
    phoneNumberId: '',
    customerId: '',
    status: '',
    endedReason: '',
    startDate: '' as string,
    endDate: '' as string,
    timeframe: 'day' as 'day' | 'week' | 'month'
  });
  
  // Dialog states
  const [selectedCall, setSelectedCall] = useState<CallLog | null>(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);

  useEffect(() => {
    if (activeTab === 0) {
      loadCallLogs();
    } else if (activeTab === 1) {
      loadStats();
    }
  }, [activeTab, page, rowsPerPage, filters]);

  const loadCallLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const params: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sortOrder: 'desc'
      };

      if (filters.assistantId) params.assistantId = filters.assistantId;
      if (filters.phoneNumberId) params.phoneNumberId = filters.phoneNumberId;
      if (filters.customerId) params.customerId = filters.customerId;
      if (filters.startDate) params.createdAtGt = filters.startDate;
      if (filters.endDate) params.createdAtLt = filters.endDate;

      const response = await axios.get(`${API_BASE_URL}/vapi/logs`, { params });
      
      if (response.data.success) {
        setLogs(response.data.data || []);
        // VAPI não retorna total count, então usamos um cálculo estimado
        setTotalCount(response.data.data?.length === rowsPerPage ? (page + 2) * rowsPerPage : (page * rowsPerPage) + response.data.data?.length);
      } else {
        setError(response.data.error || 'Erro ao carregar logs');
      }
    } catch (err: any) {
      setError('Erro ao carregar logs de ligações');
      console.error('Error loading call logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get(`${API_BASE_URL}/vapi/dashboard-stats`, {
        params: { timeframe: filters.timeframe }
      });
      
      if (response.data.success) {
        setStats(response.data.data);
      } else {
        setError(response.data.error || 'Erro ao carregar estatísticas');
      }
    } catch (err: any) {
      setError('Erro ao carregar estatísticas');
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (activeTab === 0) {
      loadCallLogs();
    } else {
      loadStats();
    }
  };

  const handleViewDetails = async (callId: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vapi/logs/${callId}`);
      if (response.data.success) {
        setSelectedCall(response.data.data);
        setOpenDetailsDialog(true);
      }
    } catch (err) {
      console.error('Error loading call details:', err);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params: any = {};
      if (filters.assistantId) params.assistantId = filters.assistantId;
      if (filters.phoneNumberId) params.phoneNumberId = filters.phoneNumberId;
      if (filters.customerId) params.customerId = filters.customerId;
      if (filters.startDate) params.createdAtGt = filters.startDate;
      if (filters.endDate) params.createdAtLt = filters.endDate;
      params.format = 'csv';

      const response = await axios.get(`${API_BASE_URL}/vapi/export-logs`, {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'vapi-call-logs.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  };

  const getStatusChip = (status: string, endedReason?: string) => {
    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
    let label = status;

    switch (status) {
      case 'ended':
        if (endedReason === 'assistant-ended-call' || endedReason === 'assistant-forwarded-call') {
          color = 'success';
          label = 'Sucesso';
        } else {
          color = 'error';
          label = 'Falha';
        }
        break;
      case 'in-progress':
        color = 'info';
        label = 'Em andamento';
        break;
      case 'ringing':
        color = 'warning';
        label = 'Tocando';
        break;
      case 'queued':
        color = 'default';
        label = 'Na fila';
        break;
      default:
        color = 'default';
    }

    return <Chip label={label} color={color} size="small" />;
  };

  const getSuccessEvaluation = (log: CallLog) => {
    if (log.status === 'ended') {
      if (log.endedReason === 'assistant-ended-call' || log.endedReason === 'assistant-forwarded-call') {
        return <Chip label="Pass" color="success" size="small" />;
      }
    }
    return <Chip label="Fail" color="error" size="small" />;
  };

  const formatDuration = (log: CallLog) => {
    if (log.startedAt && log.endedAt) {
      const duration = (new Date(log.endedAt).getTime() - new Date(log.startedAt).getTime()) / 1000;
      if (duration < 60) {
        return `${Math.round(duration)}s`;
      } else if (duration < 3600) {
        return `${Math.round(duration / 60)}m ${Math.round(duration % 60)}s`;
      } else {
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        return `${hours}h ${minutes}m`;
      }
    }
    return '-';
  };

  const formatCurrency = (value: number | undefined) => {
    return value ? `$${value.toFixed(2)}` : '$0.00';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getCallTypeIcon = (type: string) => {
    switch (type) {
      case 'outbound':
        return <CallMadeIcon color="primary" />;
      case 'inbound':
        return <CallReceivedIcon color="secondary" />;
      default:
        return <PhoneIcon />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneIcon sx={{ mr: 2 }} />
            Call Logs
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={loading}
            >
              Exportar CSV
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Logs de Ligações" />
          <Tab label="Estatísticas" />
        </Tabs>

        {activeTab === 0 && (
          <>
            {/* Filtros */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filtros
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Assistant ID"
                    value={filters.assistantId}
                    onChange={(e) => setFilters({ ...filters, assistantId: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Phone Number ID"
                    value={filters.phoneNumberId}
                    onChange={(e) => setFilters({ ...filters, phoneNumberId: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Data Início"
                    type="date"
                    value={filters.startDate || ''}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Data Fim"
                    type="date"
                    value={filters.endDate || ''}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Tabela de Logs */}
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Call ID</TableCell>
                      <TableCell>Assistente</TableCell>
                      <TableCell>Número Assistant</TableCell>
                      <TableCell>Número Cliente</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Motivo Término</TableCell>
                      <TableCell>Sucesso</TableCell>
                      <TableCell>Início</TableCell>
                      <TableCell>Duração</TableCell>
                      <TableCell>Custo</TableCell>
                      <TableCell>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center">
                          Nenhum log encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((log) => (
                        <TableRow key={log.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {log.id.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {log.assistant?.name || log.assistantId?.substring(0, 8) || '-'}
                          </TableCell>
                          <TableCell>
                            {log.phoneNumber?.number || '-'}
                          </TableCell>
                          <TableCell>
                            {log.customer?.number || '-'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {getCallTypeIcon(log.type)}
                              <Typography variant="body2" sx={{ ml: 1, textTransform: 'capitalize' }}>
                                {log.type || 'Outbound'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {getStatusChip(log.status, log.endedReason)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {log.endedReason?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {getSuccessEvaluation(log)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {log.createdAt ? formatDate(log.createdAt) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDuration(log)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatCurrency(log.cost)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Ver detalhes">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(log.id)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
              />
            </Paper>
          </>
        )}

        {activeTab === 1 && (
          <>
            {/* Filtros de Estatísticas */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Período</InputLabel>
                    <Select
                      value={filters.timeframe}
                      onChange={(e) => setFilters({ ...filters, timeframe: e.target.value as 'day' | 'week' | 'month' })}
                      label="Período"
                    >
                      <MenuItem value="day">Último dia</MenuItem>
                      <MenuItem value="week">Última semana</MenuItem>
                      <MenuItem value="month">Último mês</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Cards de Estatísticas */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : stats ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Total de Ligações
                          </Typography>
                          <Typography variant="h4">
                            {stats.total}
                          </Typography>
                        </Box>
                        <PhoneIcon color="primary" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Ligações com Sucesso
                          </Typography>
                          <Typography variant="h4" color="success.main">
                            {stats.successful}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {stats.total > 0 ? Math.round((stats.successful / stats.total) * 100) : 0}% do total
                          </Typography>
                        </Box>
                        <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Ligações com Falha
                          </Typography>
                          <Typography variant="h4" color="error.main">
                            {stats.failed}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {stats.total > 0 ? Math.round((stats.failed / stats.total) * 100) : 0}% do total
                          </Typography>
                        </Box>
                        <TrendingDownIcon color="error" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Custo Total
                          </Typography>
                          <Typography variant="h4">
                            {formatCurrency(stats.totalCost)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Média: {formatCurrency(stats.total > 0 ? stats.totalCost / stats.total : 0)}
                          </Typography>
                        </Box>
                        <AssessmentIcon color="info" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Motivos de Término
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      {Object.entries(stats.endedReasons).map(([reason, count]) => (
                        <Box key={reason} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">
                            {reason.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {count} ({stats.total > 0 ? Math.round((count / stats.total) * 100) : 0}%)
                          </Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Métricas de Desempenho
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          Duração Média
                        </Typography>
                        <Typography variant="h6">
                          {Math.round(stats.averageDuration)}s
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          Taxa de Sucesso
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {stats.total > 0 ? Math.round((stats.successful / stats.total) * 100) : 0}%
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Transferências
                        </Typography>
                        <Typography variant="h6">
                          {stats.transferred} ligações
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Alert severity="info">
                Nenhuma estatística disponível para o período selecionado.
              </Alert>
            )}
          </>
        )}

        {/* Dialog de Detalhes */}
        <Dialog
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Detalhes da Ligação
          </DialogTitle>
          <DialogContent>
            {selectedCall && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Call ID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                    {selectedCall.id}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Box sx={{ mb: 2 }}>
                    {getStatusChip(selectedCall.status, selectedCall.endedReason)}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Número do Assistente</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedCall.phoneNumber?.number || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Número do Cliente</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedCall.customer?.number || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Data/Hora Início</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedCall.createdAt ? formatDate(selectedCall.createdAt) : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Duração</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatDuration(selectedCall)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Custo</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatCurrency(selectedCall.cost)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Motivo do Término</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedCall.endedReason?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-'}
                  </Typography>
                </Grid>
                {selectedCall.messages && selectedCall.messages.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Mensagens da Conversa
                    </Typography>
                    <Paper sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                      {selectedCall.messages.map((message, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            {message.role}: 
                          </Typography>
                          <Typography variant="body2">
                            {message.message}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetailsDialog(false)}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default CallLogs;